import {Injectable} from "@angular/core";
import {AbstractBaseComponent} from "../app.baseComponent";
import {CanActivate, Router} from "@angular/router";
import {Observable} from "rxjs";
import {map, tap} from "rxjs/operators";
import {AuthenticationHelper} from "../helpers/authenticationHelper";
import {UtilityHelper} from "../helpers/utilityHelper";
import {OnboardingProgressService} from "../pages/gamification/services/onboarding-progress.service";
import { shouldRedirectToWaitingListFromStorage } from "../helpers/openAccessWaitingList.helper";
import { isTransitionedFromMentorHub } from "../pages/gamification/experience-mode";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { isPhoneVerificationMandatory, getPhoneVerificationPolicyState, shouldShowPhoneVerificationPrompt } from "../helpers/phoneVerificationPolicy.helper";
import { AppConstant } from "../app.constant";

@Injectable()
export class PreLoginGuard extends AbstractBaseComponent implements CanActivate {
    constructor(
        private authService: AuthenticationHelper,
        private utility: UtilityHelper,
        private route: Router,
        private onboarding: OnboardingProgressService,
    ) {
        super();
    }

    canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): boolean | Observable<boolean> {
        if (state?.url?.includes('/add-phone-number') || state?.url?.includes('/signout') || AppConstant.isSignOutInProgress()) {
            return true;
        }

        const token = this.utility.getItem('token');
        const userId = this.utility.getItem('userId') || this.utility.getItem('phone_verification_user_id');
        const pendingPhone = this.utility.getItem('phone_number_collection_pending') === 'true';
        const isMandatory = userId ? isPhoneVerificationMandatory(this.utility, userId) : false;
        const policyState = userId ? getPhoneVerificationPolicyState(this.utility, userId) : null;
        const isRequired = policyState?.phone_verification_required === true;
        const shouldPrompt = userId && isRequired ? shouldShowPhoneVerificationPrompt(this.utility, userId, true) : false;

        // If user is authenticated and phone verification is pending, mandatory, or scheduled for a prompt,
        // redirect directly to /add-phone-number rather than allowing them to see the public landing/home page
        if (token && (pendingPhone || isMandatory || shouldPrompt)) {
            this.route.navigate(['/add-phone-number']);
            return false;
        }

        if (!this.authService.isLoggedIn()) {
            if (
                this.utility.myDomain === this.LOCAL_DOMAIN ||
                this.utility.myDomain === this.BRILLIANTCLUB_DOMAIN ||
                this.utility.myDomain === this.KNOWLEDGE_DOMAIN
            ) {
                return true;
            } else if (this.utility.myDomain != this.MENTORING_DOMAIN) {
                this.utility.navigateToMentoringDomain();
                return false;
            }
            return true;
        }

        if (this.utility.myDomain === this.KNOWLEDGE_DOMAIN) {
            return true;
        }

        // Hub mentors who opened a project from the hub must stay in that
        // project — do not treat `/` as "send them back to Mentor Hub".
        if (
            this.utility.getItem('role') === 'mentor' &&
            this.utility.hasMentorHubAccess &&
            !isTransitionedFromMentorHub(this.utility)
        ) {
            this.route.navigate(['/mentor-hub/dashboard']);
            return false;
        }

        return this.onboarding.ensureSession().pipe(
            tap(() => this.redirectToUser()),
            map(() => false),
        );
    }

    redirectToUser() {
        const userId = this.utility.getItem('userId') || this.utility.getItem('phone_verification_user_id');
        const pendingPhone = this.utility.getItem('phone_number_collection_pending') === 'true';
        const isMandatory = userId ? isPhoneVerificationMandatory(this.utility, userId) : false;
        const policyState = userId ? getPhoneVerificationPolicyState(this.utility, userId) : null;
        const isRequired = policyState?.phone_verification_required === true;
        const shouldPrompt = userId && isRequired ? shouldShowPhoneVerificationPrompt(this.utility, userId, true) : false;

        if (pendingPhone || isMandatory || shouldPrompt) {
            this.route.navigate(['/add-phone-number']);
            return;
        }

        const showWaitingList = shouldRedirectToWaitingListFromStorage(this.utility);

        if (this.utility.myDomain == this.LOCAL_DOMAIN) {
            if (showWaitingList) {
                this.route.navigate(['/waiting-list']);
            } else {
                this.route.navigate(this.onboarding.landingRoute());
            }
        } else if (this.utility.myDomain == this.MENTORING_DOMAIN) {
            if (showWaitingList) {
                this.route.navigate(['/waiting-list']);
            } else {
                this.route.navigate(this.onboarding.landingRoute());
            }
        } else if (this.utility.myDomain == this.ADMIN_DOMAIN) {
            this.route.navigate(['/projects']);
        } else if (this.utility.myDomain == this.BRILLIANTCLUB_DOMAIN) {
            this.route.navigate(this.utility.channelsRouteCommands());
        }
    }

}
